import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  EventEmitter,
  Output
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { _HttpClient, SettingsService } from '@delon/theme';
import { NzSelectModeType, NzSelectComponent } from 'ng-zorro-antd/select';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserDefaultFormValue } from 'src/app/shared/types/user-default-settings';

import { InlineAddPanelComponent } from '../../inline-add-panel/inline-add-panel.component';
import { StockGroupAddConfigService } from '../../inline-add-panel/stock-group-add-config.service';
import { SelectBaseComponent } from '../select-base';
import { SelectInstantBaseComponent } from '../selectInstant-base';
import { SelectInstantBaseNewComponent } from '../selectInstant-base-new';

@Component({
  selector: 'app-stock-group-select',
  templateUrl: './stock-group-select.component.html',
  styles: [
    `
      /* ===== CONTAINER STYLES ===== */
      .stock-group-select-container {
        position: relative;
        width: 100%;
      }

      /* ===== ADD NEW BUTTON STYLES ===== */
      ::ng-deep .add-new-button {
        color: #000000 !important;
        font-weight: 500 !important;
        width: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 2px !important;
        background-color: transparent !important;
        border: none !important;
      }

      ::ng-deep .add-new-button .anticon {
        color: #000000 !important;
      }

      /* ===== ULTRA COMPACT DROPDOWN STYLES ===== */
      ::ng-deep .ant-select-dropdown:has(.stock-group-select-container) {
        max-height: none !important;
        overflow: hidden !important;
        width: 300px !important;
      }

      ::ng-deep
        .ant-select-dropdown:has(.stock-group-select-container)
        .ant-select-dropdown-menu {
        max-height: 200px !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
      }

      ::ng-deep
        .ant-select-dropdown:has(.stock-group-select-container)
        .ant-select-item-option {
        height: auto !important;
        min-height: 32px !important;
        padding: 4px 12px !important;
      }

      /* ===== SPECIAL OPTION HIGHLIGHTING ===== */
      ::ng-deep .ant-select-item-option-content .special-option {
        text-align: center !important;
      }

      /* ===== YELLOW HIGHLIGHT FOR SECOND ITEM ===== */
      ::ng-deep
        .dropdownSelect:not(:has(.ant-select-item-option-active))
        .ant-select-item-option:nth-child(2):not(
          .ant-select-item-option-active
        ):not(.ant-select-item-option-selected):not(:hover),
      ::ng-deep
        .dropdownSelect
        .ant-select-item-option:first-child:last-child:not(
          .ant-select-item-option-active
        ):not(.ant-select-item-option-selected):not(:hover) {
        background-color: #ffc000;
      }
    `
  ],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StockGroupSelectComponent),
      multi: true
    }
  ]
})
export class StockGroupSelectComponent
  extends SelectInstantBaseNewComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('selector') nzSelectComponent!: NzSelectComponent;

  @Input() activeOnly = true;
  @Input() mode: NzSelectModeType = 'default';
  @Input() defaultType?: 'stockgroup' = 'stockgroup';
  @Input() selectDisabled: boolean = false;
  @Input() tabindex: string | null = null;
  @Input() nzAllowClear: boolean = true;

  @Output() readonly addNewStockGroup = new EventEmitter<any>();

  NotShowInReport: boolean = false;
  destroy$ = new Subject<void>();
  private defaultUserSettings?: UserDefaultFormValue;

  stockGroupConfig: any;
  inlineDefaults: any = null;

  constructor(
    http: _HttpClient,
    router: Router,
    cdr: ChangeDetectorRef,
    private reuseTabService: ReuseTabService,
    private el: ElementRef,
    private settingsService: SettingsService,
    private stockGroupAddConfigService: StockGroupAddConfigService
  ) {
    super(http, cdr, router);
    this.valueField = 'id';
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.checkAddNewOptionInReportAccess();
    this.stockGroupConfig =
      this.stockGroupAddConfigService.getStockGroupConfig();

    // Listen to search form changes to store instant data
    this.searchForm.controls.keyword.valueChanges.subscribe(value => {
      if (value) {
        this.storedInstantData = value.trim();
      }
    });

    // Ensure the correct item is active whenever the list updates while open
    this.onListFilled.subscribe(() => {
      if ((this.nzSelectComponent as any)?.nzOpen) {
        this.ensureFirstItemActivatedAndFocused();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.tabindex) {
      const nzSelectElement =
        this.nzSelectComponent.originElement.nativeElement;
      const searchInput = nzSelectElement.querySelector('input');

      if (searchInput) {
        searchInput.setAttribute('tabindex', this.tabindex?.toString() || '0');

        // Listen to input events directly
        searchInput.addEventListener('input', (event: any) => {
          if (event.target.value) {
            this.storedInstantData = event.target.value.trim();
          }
        });
      }
    }
  }

  //!default filter
  // getUrl(searchValue: string): string {
  //   let filter = 'isActive eq true';
  //   if (this.activeOnly === false) {
  //     filter += ' or isActive eq false';
  //   }
  //   if (searchValue) {
  //     const searchStr = searchValue.replaceAll("'", "''");
  //     filter = `(${filter}) and (contains(code, '${encodeURIComponent(
  //       searchStr
  //     )}') or contains(name,'${encodeURIComponent(searchStr)}'))`;
  //   }
  //   return `api/stockgroups/query?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
  // }

  //with uds
  getUrl(searchValue: string): string {
    if (searchValue) {
      // Check if this is a valueField match request (indicated by special prefix)
      const isValueFieldMatch = searchValue.startsWith(SelectBaseComponent.VALUEFIELD_MATCH_PREFIX);
      const actualSearchValue = isValueFieldMatch
        ? searchValue.substring(SelectBaseComponent.VALUEFIELD_MATCH_PREFIX.length)
        : searchValue;

      const searchStr = actualSearchValue.replaceAll("'", "''");

      // Check if searchValue can be converted to int (ID)
      const searchValueNum = parseInt(actualSearchValue, 10);
      const isNumericId =
        !isNaN(searchValueNum) &&
        searchValueNum.toString() === actualSearchValue.trim();

      if (isValueFieldMatch) {
        // ValueField match: use eq operator for precise matching based on valueField
        // Ignore prerequisite conditions (like isActive) when matching by valueField
        // This ensures we can find records even if they are inactive
        let filter: string;
        if (this.valueField === 'id') {
          // Match by ID exactly
          filter = `id eq ${isNumericId ? searchValueNum : actualSearchValue}`;
        } else {
          // Match by the specified valueField exactly (code, name, etc.)
          // Use the valueField if set, otherwise default to 'code'
          const fieldName = this.valueField || 'code';
          filter = `${fieldName} eq '${encodeURIComponent(searchStr)}'`;
        }
        return `api/stockgroups/query?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
      } else {
        // Partial match: use contains for search behavior with prerequisite conditions
        let filter = 'isActive eq true';
        if (this.activeOnly === false) {
          filter += ' or isActive eq false';
        }
        if (isNumericId) {
          // Include ID matching: (Id eq <id>) or (code/name contains search)
          filter = `(${filter}) and ((id eq ${searchValueNum}) or (contains(code, '${encodeURIComponent(searchStr)}') or contains(name,'${encodeURIComponent(searchStr)}')))`;
        } else {
          // Original behavior: only search by code/name
          filter = `(${filter}) and (contains(code, '${encodeURIComponent(searchStr)}') or contains(name,'${encodeURIComponent(searchStr)}'))`;
        }
        return `api/stockgroups/query?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
      }
    } else {
      // No search value: apply prerequisite conditions
      let filter = 'isActive eq true';
      if (this.activeOnly === false) {
        filter += ' or isActive eq false';
      }
      return `api/stockgroups/query?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
    }
  }

  // set the location want to navigate
  jump(): string {
    return `/stk/group`;
  }

  // set the param
  queryParam(): String {
    return 'StockGroupCode';
  }

  checkAddNewOptionInReportAccess(): boolean {
    let curUrl = this.reuseTabService.curUrl.toLowerCase();

    if (curUrl.includes('report') || curUrl.includes('einv/')) {
      this.NotShowInReport = true;
    } else {
      this.NotShowInReport = false;
    }

    return this.NotShowInReport;
  }

  override async writeValue(value: any) {
    // Extract ID if value is an object (e.g., from API response)
    let idValue = value;
    if (value && typeof value === 'object' && this.valueField) {
      idValue = value[this.valueField] || value.id;
    }
    
    this.value = idValue;

    // If use selected item as value
    if (this.value) {
      if (!this.valueField) {
        this.ensureItemPresentList(value);
        this.selected = value;
      } else {
        // When valueField is set, we need to fetch the list to find the matching item
        // Use valueField match to find the record that exactly matches the valueField (ID or Code)
        // Prefix keyword with special marker to indicate valueField match (ignores prerequisite conditions)
        let keyword = `${SelectBaseComponent.VALUEFIELD_MATCH_PREFIX}${idValue}`;
        await this.fetchData(keyword);
        this.selected = this.list.find(x => x[this.valueField] == idValue);
      }
    } else {
      this.selected = null;
      //this.list = [];
    }
  }

  // Override the addNew method to show inline panel instead of navigation
  override addNew(): void {
    // Don't capture again if we already have stored data from button click
    if (!this.storedInstantData) {
      this.captureCurrentSearchValue();
    }

    // Get the current search value as fallback only if we don't have stored data
    if (!this.storedInstantData) {
      const currentSearchValue = this.getCurrentSearchValue();
      if (currentSearchValue) {
        this.storedInstantData = currentSearchValue;
      }
    }

    // Load options and defaults first, then show panel
    Promise.all([this.loadSelectOptions(), this.fetchInlineDefaults()]).then(
      () => {
        this.showInlineAddPanel = true;
        this.inlineAddPanel.changeInstantData(this.storedInstantData);
        if (this.nzSelectComponent) {
          this.nzSelectComponent.setOpenState(false);
        }
      }
    );
  }

  onStockGroupCreated(event: { item: any; selectionConfig?: any }): void {
    if (event.item && event.selectionConfig) {
      // Create the stock group object in the expected format
      const stockGroup = {
        id: event.item[event.selectionConfig.valueField],
        code: event.item.code || event.item.stockGroupCode,
        name: event.item[event.selectionConfig.labelField],
        isActive: event.item.isActive
      };

      // Add the new stock group to the list and select it
      this.list.unshift(stockGroup);

      // Close the inline panel first
      this.showInlineAddPanel = false;

      // Set the stock group object in the form control to trigger stockGroup.valueChanges
      this.value = stockGroup.id;
      this.selected = stockGroup.id;

      // Set the stock group object directly in the form control
      this.onChange(stockGroup);
      this.writeValue(stockGroup);
      // Emit addNewStockGroup event for parent component to handle
      this.addNewStockGroup.emit(stockGroup);

      // Clear stored instant data after successful creation
      this.clearStoredInstantData();

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  onStockGroupCancelled(): void {
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }

  onStockGroupMoreDetails(): void {
    // Navigate to stock group details page with all the current form data
    const formData = this.inlineAddPanel?.formGroup?.value;
    if (formData) {
      // Create query params with all form fields
      const queryParams: any = {
        autoPopulate: 1
      };

      // Add all form fields to query params
      if (formData.name) queryParams.StockGroupName = formData.name;
      if (formData.code) queryParams.StockGroupCode = formData.code;
      if (formData.isActive !== undefined)
        queryParams.IsActive = formData.isActive;

      queryParams.autoPopulate = 1;

      this.router.navigate(['/stk/group'], {
        queryParams: queryParams
      });
    } else {
      this.router.navigate(['/stk/group'], {
        queryParams: {
          autoPopulate: 1
        }
      });
    }
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }

  onFieldChange(event: { fieldName: string; value: any }): void {
    // Handle field changes - auto-code component will handle code generation
  }

  private loadSelectOptions(): Promise<void> {
    // For stock group, we don't need to load additional options
    return Promise.resolve();
  }

  private fetchInlineDefaults(): Promise<void> {
    // Call server to get defaults similar to stock group/detail new()
    return new Promise(resolve => {
      this.http.get('api/stockgroups/new').subscribe({
        next: (data: any) => {
          const defaults: any = {};
          if (data?.isActive !== undefined) defaults.isActive = data.isActive;
          this.inlineDefaults = defaults;
          resolve();
        },
        error: () => {
          this.inlineDefaults = null;
          resolve();
        }
      });
    });
  }
}
